import React, { SyntheticEvent, useId } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { Icon } from '@fikasio/react-ui-components';
import styles from './FilterModal.module.css';

export interface FilterSelectOption {
  color?: string;
  icon?: React.ComponentProps<typeof Icon>['name'];
  label: string;
  value: string;
}

interface FilterSelectProps {
  label: string;
  onChange: (value: string) => void;
  options: FilterSelectOption[];
  value: string;
}

const optionIconStyle = (color?: string): React.CSSProperties => ({
  color,
  flexShrink: 0,
  marginRight: 10,
  width: 18,
});

const OptionContent = ({ option }: { option: FilterSelectOption }) => (
  <span className={styles.option}>
    {
      option.icon && (
        <Icon
          name={option.icon}
          style={optionIconStyle(option.color)}
        />
      )
    }
    <span className={styles.optionLabel}>{option.label}</span>
  </span>
);

interface FilterSelectToggleProps {
  'aria-expanded'?: boolean;
  children?: React.ReactNode;
  className?: string;
  id?: string;
  label: string;
  onClick?: (event: SyntheticEvent) => void;
}

const FilterSelectToggle = React.forwardRef<HTMLButtonElement, FilterSelectToggleProps>(({
  'aria-expanded': ariaExpanded,
  children,
  className,
  id,
  label,
  onClick,
  ...props
}, ref) => (
  <button
    {...props}
    aria-expanded={ariaExpanded}
    aria-haspopup="menu"
    aria-label={label}
    className={`form-select ${styles.toggle}${className ? ` ${className}` : ''}`}
    id={id}
    onClick={event => {
      event.preventDefault();
      onClick?.(event);
    }}
    ref={ref}
    type="button"
  >
    {children}
  </button>
));

FilterSelectToggle.displayName = 'FilterSelectToggle';

const FilterSelect = ({
  label,
  onChange,
  options,
  value,
}: FilterSelectProps) => {
  const id = useId();
  const selected = options.find(option => option.value === value) ?? options[0];

  return (
    <div className={styles.field}>
      <label htmlFor={id}>{label}</label>
      <Dropdown className={styles.dropdown}>
        <Dropdown.Toggle
          as={FilterSelectToggle as React.ElementType}
          id={id}
          label={label}
        >
          {
            selected && (
              <OptionContent option={selected} />
            )
          }
        </Dropdown.Toggle>
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              { name: 'hide', enabled: false },
              { name: 'offset', options: { offset: [0, 6] } },
            ],
          }}
        >
          {
            options.map(option => (
              <Dropdown.Item
                active={option.value === value}
                key={option.value}
                onClick={() => onChange(option.value)}
              >
                <OptionContent option={option} />
              </Dropdown.Item>
            ))
          }
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default FilterSelect;
